import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {NestedFrameSlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/nestedFrameSlot/NestedFrameSlot';
import {
    AnySheet,
    IAnySheetSnapshot,
    SheetColumnName,
    SheetName
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {ISlotSnapshot, Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotLocation, SlotLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId, SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, observable, toJS} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import _isEqual from 'lodash/isEqual';

export enum SlotSortOrder
{
    ASC = 1,
    DESC = -1
}

export interface ISlotsSnapshot
{
    [slotId:string]:ISlotSnapshot;
}

export interface ISlotLocationToSlotsSnapshot
{
    [slotLocationKey:string]:SlotId;
}

export interface ISheetSnapshot extends IAnySheetSnapshot
{
    slots:ISlotsSnapshot;
    slotLocationToSlot:ISlotLocationToSlotsSnapshot;
}

export class Sheet extends AnySheet
{
    @observable
    public slots:Map<SlotId, Slot | NestedFrameSlot>;

    @observable
    public slotLocationToSlot:Map<SlotLocationKey, SlotId>;

    private constructor()
    {
        super();
    }

    public static create(id:SheetId,
                         workbook:Workbook,
                         name:SheetName,
                         columnNames:Array<SheetColumnName> = [],
                         slots:Map<SlotId, Slot> = new Map(),
                         slotLocationToSlot:Map<SlotLocationKey, SlotId> = new Map()):Sheet
    {
        const sheet:Sheet = new Sheet();

        sheet.id = id;
        sheet.workbook = workbook;
        sheet.name = name;
        sheet.columnNames = columnNames;
        sheet.slots = slots;
        sheet.slotLocationToSlot = slotLocationToSlot;

        return sheet;
    }

    public clone():Sheet
    {
        const clonedSheet:Sheet = new Sheet();

        clonedSheet.id = IdUtil.newGUID();
        clonedSheet.workbook = this.workbook;
        clonedSheet.name = this.name;
        clonedSheet.columnNames = this.columnNames;

        const clonedSlots:Map<SlotId, Slot> = new Map();
        const clonedSlotLocationToSlots:Map<SlotLocationKey, SlotId> = new Map();

        Array.from(this.slots.values()).forEach((slot:Slot) =>
        {
            const clonedSlot:Slot = slot.clone(clonedSheet);

            clonedSlots.set(clonedSlot.id, clonedSlot);
            clonedSlotLocationToSlots.set(SlotLocation.toKey(clonedSlot.location), clonedSlot.id);
        });

        clonedSheet.slots = clonedSlots;
        clonedSheet.slotLocationToSlot = clonedSlotLocationToSlots;

        return clonedSheet;
    }

    public get isCloud():boolean
    {
        return false;
    }

    public getSlotByLocation(location:SlotLocation):Slot | null
    {
        if( !location )
            return null;

        const slotId:SlotId = this.slotLocationToSlot.get(location.toKey());

        return slotId ? this.slots.get(slotId) as Slot || null : null;
    }

    public getSlots():Slot[]
    {
        return Array.from(this.slots.values()) as Array<Slot>;
    }

    @action
    public addSlot(slot:Slot):void
    {
        if( slot && slot.location )
        {
            this.slotLocationToSlot.set(SlotLocation.toKey(slot.location), slot.id);
            this.slots.set(slot.id, slot);
        }
        else
            mk.error(area.Slot, 'Slot or SlotLocation is not defined. Slot:', slot);
    }

    @action
    public deleteSlot(slotId:SlotId):void
    {
        if( this.slots.has(slotId) )
        {
            const slot:Slot = this.slots.get(slotId) as Slot;

            this.slotLocationToSlot.delete(slot.location.toKey());
            this.slots.delete(slot.id);
        }
        else
            mk.error(area.Slot, 'Slot is not found. SlotId:', slotId);
    }

    @action
    public setSlotLocation(slotId:SlotId, location:SlotLocation):void
    {
        if( this.slots.has(slotId) )
        {
            if( location )
            {
                const slot:Slot = this.slots.get(slotId) as Slot;

                this.slotLocationToSlot.delete(slot.location.toKey());
                slot.setLocation(location);
                this.slotLocationToSlot.set(location.toKey(), slot.id as any);
            }
            else
                mk.error(area.Slot, 'SlotLocation is not defined.');
        }
        else
            mk.error(area.Slot, 'Slot is not found. SlotId:', slotId);
    }

    public static toJson:ITransformer<Sheet, ISheetSnapshot> = createTransformer((sheet:Sheet) =>
    {
        return {
            id: sheet.id,
            name: sheet.name,
            columnNames: toJS(sheet.columnNames),
            slots: Array.from(sheet.slots.entries()).reduce((result, [slotId, slot]) =>
            {
                result[slotId] = Slot.toJson(slot as Slot);
                return result;
            }, {}),
            slotLocationToSlot: Array.from(sheet.slotLocationToSlot.entries())
                .reduce((result, [slotLocationKey, slotId]) =>
                {
                    result[slotLocationKey] = slotId;
                    return result;
                }, {})
        };
    });

    public static fromJson(json:ISheetSnapshot,
                           workbook:Workbook,
                           currentState?:Sheet,
                           currentJson?:ISheetSnapshot,
                           ctx?:DesignStateFromJsonCtx):Sheet
    {
        if( !currentState )
            return Sheet.fromJsonDeep(json, workbook);

        if( !currentJson )
            currentJson = Sheet.toJson(currentState);

        const sheet:Sheet = new Sheet();

        sheet.id = json.id;
        sheet.workbook = workbook;
        sheet.name = json.name;
        sheet.columnNames = json.columnNames;

        if( json.slots === currentJson.slots )
        {
            sheet.slots = currentState.slots;
            sheet.slots.forEach((slot:Slot) => slot.sheet = sheet);
        }
        else
        {
            const slots:Map<SlotId, Slot> = new Map();

            Object.entries(json.slots).forEach(([slotId, slot]) =>
            {
                const slotCurrentState:Slot = currentState.slots.get(slotId) as Slot;
                const slotCurrentJson:ISlotSnapshot = currentJson.slots[slotId];

                if(
                    json.slots[slotId] === currentJson.slots[slotId] ||
                    (
                        // doing this check because after undo/redo references might change although objects actually didn't.
                        // this is a side effect of working with snapshots instead of patches.
                        // doing this specifically for slots since changed slots should be reevaluated, we want to avoid unnecessary reevaluations
                        _isEqual(json.slots[slotId], currentJson.slots[slotId])
                    )
                )
                {
                    slotCurrentState.sheet = sheet;
                    slots.set(slotId, slotCurrentState);
                }
                else
                    slots.set(slotId, Slot.fromJson(slot, sheet, slotCurrentState, slotCurrentJson, ctx));
            });

            sheet.slots = slots;

            if( ctx )
            {
                const slotList:Slot[] = Array.from(sheet.slots.values()) as Slot[];
                const currentSlotList:Slot[] = Array.from(currentState.slots.values()) as Slot[];

                const addedSlots = slotList.filter((target:Slot) => (
                    !currentSlotList.some((slot:Slot) => slot === target)
                ));

                const removedSlots = currentSlotList.filter((target:Slot) => (
                    !slotList.some((slot:Slot) => slot === target)
                ));

                ctx.addedSlots.push(...addedSlots);
                ctx.removedSlots.push(...removedSlots);
            }
        }

        if( json.slotLocationToSlot === currentJson.slotLocationToSlot )
            sheet.slotLocationToSlot = currentState.slotLocationToSlot;
        else
        {
            const slotLocationToSlot:Map<SlotLocationKey, SlotId> = new Map();

            Object.entries(json.slotLocationToSlot)
                .forEach(([slotLocationKey, slotId]) => slotLocationToSlot.set(slotLocationKey, slotId));

            sheet.slotLocationToSlot = slotLocationToSlot;
        }

        return sheet;
    }

    public static fromJsonDeep(json:ISheetSnapshot, workbook:Workbook):Sheet
    {
        const sheet:Sheet = new Sheet();

        const slots:Map<SlotId, Slot> = new Map();
        const slotLocationToSlot:Map<SlotLocationKey, SlotId> = new Map();

        sheet.id = json.id;
        sheet.workbook = workbook;
        sheet.name = json.name;
        sheet.columnNames = json.columnNames;

        Object.entries(json.slots)
            .forEach(([slotId, slot]) => slots.set(slotId, Slot.fromJsonDeep(slot, sheet)));

        Object.entries(json.slotLocationToSlot)
            .forEach(([slotLocationKey, slotId]) => slotLocationToSlot.set(slotLocationKey, slotId));

        sheet.slots = slots;
        sheet.slotLocationToSlot = slotLocationToSlot;

        return sheet;
    }
}
